import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

/* ================= Helpers ================= */

// NWD
function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

// uprość ułamek (kontrola znaku w liczniku)
function simplifyFrac(p, q) {
  if (q < 0) { p = -p; q = -q; }
  const g = gcd(Math.abs(p), Math.abs(q));
  return [p / g, q / g];
}

// LaTeX: znak PRZED ułamkiem
function fracLatexSigned(p, q) {
  if (q === 1) return `${p}`;
  const neg = (p < 0) !== (q < 0);
  const A = Math.abs(p), B = Math.abs(q);
  return `${neg ? "-" : ""}\\tfrac{${A}}{${B}}`;
}

// najlepsza postać: całkowita / mieszany / zwykły
function bestLatexFromNumDen(p, q) {
  [p, q] = simplifyFrac(p, q);
  if (q === 1) return `${p}`;
  const absP = Math.abs(p), absQ = Math.abs(q);
  const neg = p < 0;
  if (absP > absQ) {
    const whole = Math.floor(absP / absQ);
    const rem = absP % absQ;
    if (rem === 0) return `${neg ? "-" : ""}${whole}`;
    return `${neg ? "-" : ""}${whole}\\,\\tfrac{${rem}}{${absQ}}`;
  }
  return `${neg ? "-" : ""}\\tfrac{${absP}}{${absQ}}`;
}

// formater: 1x -> x, -1x -> -x
const coeffX = (k) => (k === 1 ? "x" : k === -1 ? "-x" : `${k}x`);
const pm = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);

/* ================ GENERATE TASK ================ */
export function generateTask() {
  let a, b, c, d, e, f, x;

  const getRandom = (min, max, excludeZero = false) => {
    let val;
    do {
      val = Math.floor(Math.random() * (max - min + 1)) + min;
    } while (excludeZero && val === 0);
    return val;
  };

  const MAX_ATTEMPTS = 100;
  let attempts = 0;

  while (attempts < MAX_ATTEMPTS) {
    attempts++;

    a = getRandom(1, 4);
    c = getRandom(1, 4);
    b = getRandom(-4, 4, true);
    d = getRandom(-4, 4, true);

    const ac = a * c;
    const possibleE = [1, 2, 3, 4].filter(val => val * val === ac);
    if (possibleE.length === 0) continue;
    e = possibleE[Math.floor(Math.random() * possibleE.length)];

    f = getRandom(-5, 5, true);

    const leftLinear = a * d + b * c;
    const leftConst  = b * d;
    const rightLinear = 2 * e * f;
    const rightConst  = f * f;

    const numerator   = rightConst - leftConst;     // f^2 - bd
    const denominator = leftLinear - rightLinear;   // (ad+bc) - 2ef

    if (denominator === 0 || !isFinite(numerator / denominator)) continue;

    const g = gcd(numerator, denominator);
    const numSimple = numerator / g;
    const denSimple = denominator / g;

    if (Math.abs(numSimple) > 50 || Math.abs(denSimple) > 20) continue;

    x = numerator / denominator;

    return {
      type: "SolveQuadraticsThatReduceToLinearTask",
      data: {
        a, b, c, d, e, f,
        x,
        numerator: numSimple,
        denominator: denSimple
      }
    };
  }

  // fallback
  return {
    type: "SolveQuadraticsThatReduceToLinearTask",
    data: {
      a: 1, b: 1, c: 1, d: -1, e: 1, f: 1,
      x: 0,
      numerator: 0,
      denominator: 1
    }
  };
}

/* ================ RENDER TASK ================ */
export function renderTask({ data }) {
  const { a, b, c, d, e, f } = data;
  const left  = `(${coeffX(a)} ${pm(b)})(${coeffX(c)} ${pm(d)})`;
  const right = `(${coeffX(e)} ${pm(f)})^2`;
  return (
    <p>
      Rozwiąż równanie: <BlockMath math={`${left} = ${right}`} />
    </p>
  );
}

/* ================ INPUT ================ */
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-4">
      <span><InlineMath math="x = " /></span>
      <FractionInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="x"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const parsed = userAnswer?.parsed;
  const expected = data.numerator / data.denominator;
  const actual = parsed ?? NaN;
  const isCorrect = parsed != null && !isNaN(parsed) && Math.abs(parsed - expected) < 0.01;
  return { isCorrect, expected, actual };
}

/* ================ RENDER ANSWER ================ */
export function renderAnswer({ data }) {
  const { numerator, denominator } = data;
  return <InlineMath math={`x = ${bestLatexFromNumDen(numerator, denominator)}`} />;
}

/* ================ RENDER EXPLANATION ================ */
export function renderExplanation({ data }) {
  const { a, b, c, d, e, f, numerator, denominator } = data;

  // rozwiniecie
  const ac    = a * c;
  const ad_bc = a * d + b * c;
  const bd    = b * d;

  const e2  = e * e;           // = ac
  const ef2 = 2 * e * f;
  const f2  = f * f;

  // oryginal
  const leftStart  = `(${coeffX(a)} ${pm(b)})(${coeffX(c)} ${pm(d)})`;
  const rightStart = `(${coeffX(e)} ${pm(f)})^2`;

  // po rozwinieciu (x^2 + x + const) po obu stronach
  const leftRaw  = `${ac}x^2 ${ad_bc === 0 ? "" : ` ${pm(ad_bc)}x`} ${bd === 0 ? "" : ` ${pm(bd)}`}`.trim();
  const rightRaw = `${e2}x^2 ${ef2 === 0 ? "" : ` ${pm(ef2)}x`} ${f2 === 0 ? "" : ` ${pm(f2)}`}`.trim();

  // po skasowaniu x^2: dostajemy liniowe
  const linLeft  = `${ad_bc === 0 ? "0" : `${ad_bc}x`}${bd === 0 ? "" : ` ${pm(bd)}`}`;
  const linRight = `${ef2 === 0 ? "0" : `${ef2}x`}${f2 === 0 ? "" : ` ${pm(f2)}`}`;

  // przeniesienia
  const verbX = ef2 >= 0 ? "Odejmij obustronnie" : "Dodaj obustronnie";
  const opX   = ef2 >= 0 ? `- ${Math.abs(ef2)}x` : `+ ${Math.abs(ef2)}x`;

  const verbC = bd >= 0 ? "Odejmij obustronnie" : "Dodaj obustronnie";
  const opC   = bd >= 0 ? `- ${Math.abs(bd)}` : `+ ${Math.abs(bd)}`;

  // licznik / mianownik i ich zapis
  const num     = f2 - bd;          // = numerator * g  (po ewentualnym skróceniu w generatorze)
  const den     = ad_bc - ef2;      // = denominator * g
  const denPar  = den < 0 ? `(${den})` : `${den}`;
  const rawFrac = fracLatexSigned(num, den);
  const best    = bestLatexFromNumDen(numerator, denominator);
  const showBest = rawFrac !== best;

  return (
    <div className="space-y-3" style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p><strong>Krok 1:</strong> Rozwiń nawiasy po obu stronach.</p>
      <BlockMath math={`${leftStart} = ${rightStart}`} />
      <BlockMath math={`${leftRaw} = ${rightRaw}`} />

      <p><strong>Krok 2:</strong> Usuń składnik <InlineMath math="x^2" /> po obu stronach.</p>
      <BlockMath math={`${leftRaw} = ${rightRaw}\\;\\Big|\\ ${ac >= 0 ? "- " + ac + "x^2" : "+ " + Math.abs(ac) + "x^2"}`} />
      <BlockMath math={`${linLeft} = ${linRight}`} />

      <p><strong>Krok 3:</strong> {verbX} {Math.abs(ef2)}<InlineMath math="x" />.</p>
      <BlockMath math={`${linLeft} = ${linRight}\\;\\Big|\\ ${opX}`} />
      <BlockMath math={`${ad_bc - ef2}x ${bd === 0 ? "" : ` ${pm(bd)}`} = ${f2}`} />

      <p><strong>Krok 4:</strong> {verbC} {Math.abs(bd)}.</p>
      <BlockMath math={`${ad_bc - ef2}x ${bd === 0 ? "" : ` ${pm(bd)}`} = ${f2}\\;\\Big|\\ ${opC}`} />
      <BlockMath math={`${ad_bc - ef2}x = ${num}`} />

      <p><strong>Krok 5:</strong> Podziel obie strony przez <InlineMath math={denPar} />.</p>
      <BlockMath math={`${ad_bc - ef2}x = ${num}\\;\\Big|\\ :\\ ${denPar}`} />
      <BlockMath math={`x = ${rawFrac}`} />
      {showBest && <BlockMath math={`x = ${best}`} />}
    </div>
  );
}

/* ================ RENDER USER ANSWER ================ */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  const latex = formatLatexPreview(userAnswer.raw, "x");
  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={latex} />
    </div>
  );
}
